import { useState } from "react";
import cn from "classnames";
import { Divider, Select } from "antd";
import { Button, ButtonColorType, ButtonSize } from "@/ui-components/commonComponents/Button/Button";
import { DeleteOutlined } from "@ant-design/icons";

import { Loading, LoadingSize } from "@/shared/components/Loading/Loading";
import { TestDisplayName } from "@/shared/components/TestDisplayName/TestDisplayName";
import { isSubstring } from "@/shared/utils/comparison/comparison";

import { ITestElement } from "@/modules/assessments/types/test.interface";
import { INorm, INormSummary } from "@/shared/services/norms/norms-http.service";
import { getTestDisplayName } from "@/shared/services/tests/tests.utils";

import { QuestionItem } from "../QuestionItem/QuestionItem";
import { getNextElementStartSectionConfig } from "../utils/testElements.utils";

import { ReactComponent as DraggerSVG } from "@/assets/icons/more-horizontal.svg";
import { ReactComponent as ChevronDownSVG } from "@/assets/icons/feather-icons/chevron-down.svg";

import style from "./style.module.css";

export interface ITestItemProps {
  key?: number;
  id: number;
  name: string;
  internalName?: string;
  normId?: number;
  elementList: ITestElement[];
  onDeleteClick?: any;
  canExpandElements?: boolean;
  isChangeable?: boolean;
  norms?: INorm[] | INormSummary[];
  isLoading?: boolean;
  onNormChange?: (normId: number) => void;
  hasNormError?: boolean;
}

export function TestItem({
  id,
  name,
  internalName,
  normId,
  elementList,
  onDeleteClick,
  canExpandElements = false,
  isChangeable = false,
  norms,
  isLoading = false,
  onNormChange,
  hasNormError = false,
}: ITestItemProps) {
  const [isShownQuestionList, setIsShownQuestionList] = useState<boolean>(false);

  const handleQuestionClick = () => {
    setIsShownQuestionList((prevState) => !prevState);
  };

  const handleDeleteItemClick = () => {
    onDeleteClick && onDeleteClick(id);
  };

  return (
    <div className={style.container}>
      <div className={style.draggerContainer}>
        <DraggerSVG />
      </div>
      <div className={style.inRow}>
        <div style={{ alignContent: "center" }}>
          <p className={style.title}>
            <TestDisplayName name={name} internalName={internalName} />
          </p>
        </div>
        {isChangeable && (
          <Button
            className={style.deleteButton}
            colorType={ButtonColorType.transparentDark}
            buttonSize={ButtonSize.XSmall}
            onClick={handleDeleteItemClick}
            aria-label={name ? `Remove ${getTestDisplayName({ name, internalName })}` : "Remove test"}
            icon={<DeleteOutlined className={style.deleteIcon} />}
          />
        )}
      </div>

      <Divider />

      {isLoading ? (
        <Loading size={LoadingSize.default} />
      ) : (
        <div className={style.inRow}>
          <div className={style.testOptions}>
            <Button
              colorType={ButtonColorType.transparentDark}
              buttonSize={ButtonSize.large}
              onClick={handleQuestionClick}
              disabled={!canExpandElements}
              rightIcon={
                <ChevronDownSVG
                  className={cn(style.arrow, { [style.arrowDown]: isShownQuestionList })}
                  aria-hidden="true"
                />
              }
            >
              Elements ({elementList ? elementList.length : 0})
            </Button>
            {norms && norms.length !== 0 && (
              <Select<number>
                showSearch={true}
                disabled={!isChangeable}
                placeholder="Select a test norm"
                options={norms
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((norm) => {
                    return { value: norm.normId, label: norm.name };
                  })}
                optionFilterProp="label"
                filterOption={(input, option) => isSubstring(String(option?.label), input)}
                onChange={onNormChange}
                value={normId}
                className={style.normSelector}
                status={hasNormError ? "error" : undefined}
              />
            )}
          </div>
        </div>
      )}

      {isShownQuestionList && elementList && (
        <div className={style.questionContainer}>
          {elementList
            .sort((a, b) => a.rank - b.rank)
            .map((element) => {
              return (
                <QuestionItem
                  key={element.config.key}
                  nextElementStartSectionConfig={getNextElementStartSectionConfig(elementList, element.config.key)}
                  testElement={element}
                  numberOfQuestions={elementList?.length}
                />
              );
            })}
        </div>
      )}
    </div>
  );
}
